'use client'

import { useMemo, useRef } from 'react'
import { Swiper as SwiperClass } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/pagination'
import classNames from 'classnames'

type Props<T = unknown> = {
  id: string
  items?: T[]
  groupItemCount: number
  render: (item: T, index: number) => React.ReactNode
  keyFn?: (item: T) => number | string
  direction?: 'horizontal' | 'vertical'
  className?: string
  slideClassName?: string
}

export const GroupSlider = <T,>({
  id,
  items,
  groupItemCount,
  render,
  keyFn,
  direction = 'vertical',
  className,
  slideClassName,
}: Props<T>) => {
  const ref = useRef<SwiperClass>()
  const modules = [Pagination]

  const groups = useMemo(() => {
    return (
      items?.reduce(
        (acc, cur) => {
          if (acc[acc.length - 1]!.length === groupItemCount) {
            acc.push([])
          }

          acc[acc.length - 1]!.push(cur)

          return acc
        },
        [[]] as T[][],
      ) || []
    )
  }, [items, groupItemCount])

  if (!items?.length) {
    return null
  }

  return (
    <div
      className={classNames('relative flex items-center max-w-full', className)}
    >
      <div className="flex-1 max-w-full overflow-hidden h-full">
        <style>
          {`
          html.theme-dark .swiper-custom-pagination-bullet-active { background: white !important; }
          html.theme-light .swiper-custom-pagination-bullet-active { background: #4c4c4c !important; }
          html.theme-dark .swiper-custom-pagination-bullet { background: #4c4c4c; }
          html.theme-light .swiper-custom-pagination-bullet { background: #dddddd; }
          .swiper-custom-pagination-bullet-active { opacity: 1 }
          .swiper-custom-pagination-bullet { width: 8px; height: 8px; border-radius: 9999px }
        `}
        </style>
        <Swiper
          onInit={(swiper) => {
            ref.current = swiper
          }}
          modules={modules}
          pagination={{
            enabled: items.length > groupItemCount,
            el: `#${id}-swiper-pagination`,
            type: 'bullets',
            clickable: true,
            bulletActiveClass: 'swiper-custom-pagination-bullet-active',
            bulletClass: 'swiper-custom-pagination-bullet',
          }}
          threshold={5}
          spaceBetween={12}
          autoHeight
        >
          {(groups || []).map((data, i) => {
            return (
              <SwiperSlide key={i}>
                <div
                  className={classNames('flex gap-3', slideClassName, {
                    'flex-row justify-stretch': direction === 'horizontal',
                    'flex-col': direction !== 'horizontal',
                  })}
                >
                  {data.map((item, j) => {
                    return (
                      <div
                        style={{
                          width:
                            direction === 'horizontal'
                              ? `calc((100% - ${
                                  groupItemCount * 5
                                }px) / ${groupItemCount})`
                              : '100%',
                        }}
                        key={keyFn?.(item) ?? j}
                      >
                        {render(item, i * groupItemCount + j)}
                      </div>
                    )
                  })}
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>

        {groups.length > 1 && (
          <div
            id={`${id}-swiper-pagination`}
            className="flex justify-center pt-4 gap-2"
          />
        )}
      </div>
    </div>
  )
}
